import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";

export default function Hero({ onBook }) {
  const { t, r } = useLang();

  return (
    <section className="relative h-[100svh] min-h-[640px] w-full overflow-hidden" data-testid="hero-section">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="/images/hotel-exterior.png"
          alt="Hotel Alpi Resort, Turin"
          className="w-full h-full object-cover slow-zoom"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/90" />
      </div>

      {/* Content */}
      <div className="on-image relative z-10 h-full container-x flex flex-col justify-end pb-24 lg:pb-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="overline mb-6"
        >
          {t.hero.overline}
        </motion.div>

        <h1 className="font-serif text-4xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl">
          {t.hero.title.split("\n").map((line, i) => (
            <motion.div
              key={line || `hero-line-${i}`}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 + i * 0.12, duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
              className={i === 1 ? "italic text-[#C5A059]" : ""}
            >
              {line}
            </motion.div>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="mt-8 max-w-xl text-base lg:text-lg text-white/80 leading-relaxed"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            onClick={onBook}
            data-testid="hero-book-button"
            className="btn-gold px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium inline-flex items-center gap-3 rounded-none"
          >
            {t.cta.book} <ArrowRight size={16} weight="bold" />
          </button>
          <Link
            to={r.rooms}
            data-testid="hero-explore-button"
            className="btn-ghost px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium inline-flex items-center gap-3"
          >
            {t.cta.explore} <ArrowRight size={16} />
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="mt-10 pt-8 border-t border-white/15 max-w-xl text-xs text-white/60 tracking-wide"
        >
          {t.hero.micro}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 6, 0] }}
        transition={{ delay: 1.5, duration: 2, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/50"
      >
        <ArrowDown size={18} />
      </motion.div>
    </section>
  );
}
